import axios from 'axios';
import api, {
  clearAuthTokens,
  getCsrfHeaders,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  type AuthenticationMode,
} from './api';

export interface UserProfile {
  id: number;
  username?: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  must_change_password: boolean;
}

interface LoginResponse {
  access: string;
  access_token: string;
  refresh?: string;
  refresh_token?: string;
  user: UserProfile;
}

export const authService = {
  login: async (
    identifier: string,
    password: string,
    mode: AuthenticationMode = 'email',
  ): Promise<LoginResponse> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      const credentials = mode === 'trial_username'
        ? { username: identifier, password }
        : { email: identifier, password };
      const response = await api.post<LoginResponse>(
        '/auth/login/',
        credentials,
        { headers: csrfHeaders },
      );
      setAccessToken(response.data.access_token || response.data.access);
      setRefreshToken(response.data.refresh || response.data.refresh_token || null);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Login failed: Network or server error');
    }
  },

  logout: async (): Promise<void> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      await api.post('/auth/logout/', {}, { headers: csrfHeaders });
    } catch {
      // Logout even if the request fails
    }
    clearAuthTokens();
  },

  refreshToken: async (): Promise<string | null> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      const storedRefreshToken = getRefreshToken();
      const response = await api.post(
        '/auth/token/refresh/',
        storedRefreshToken ? { refresh: storedRefreshToken } : {},
        { headers: csrfHeaders },
      );
      const token = response.data.access_token || response.data.access;
      const rotatedRefreshToken = response.data.refresh || response.data.refresh_token;
      setAccessToken(token);
      if (rotatedRefreshToken) {
        setRefreshToken(rotatedRefreshToken);
      }
      return token;
    } catch {
      clearAuthTokens();
      return null;
    }
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      await api.post(
        '/auth/change-password/',
        { current_password: currentPassword, new_password: newPassword },
        { headers: csrfHeaders },
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to change password');
    }
  },

  getUserProfile: async (): Promise<UserProfile> => {
    try {
      const response = await api.get<UserProfile>('/auth/user/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to get profile');
    }
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      await api.post('/auth/password-reset/', { email }, { headers: csrfHeaders });
    } catch (error) {
      // Always succeed from the user's perspective to prevent email enumeration
    }
  },

  confirmPasswordReset: async (uid: string, token: string, newPassword: string): Promise<void> => {
    try {
      const csrfHeaders = await getCsrfHeaders();
      await api.post(
        '/auth/password-reset/confirm/',
        { uid, token, new_password: newPassword },
        { headers: csrfHeaders },
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to reset password');
    }
  },

};
